import React from "react";
import { Icon } from "@/lib";

// Shared workspace menu: outside click, arrow navigation and explicit focus return.
export interface MenuItem { icon?: string; label?: string; onClick?: () => void; danger?: boolean; sep?: boolean; disabled?: boolean; checked?: boolean }
export function Menu({
  trigger,
  triggerRef,
  items,
  align = "right",
  restoreFocusOnSelect = false,
}: {
  trigger: React.ReactElement;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  items: MenuItem[];
  align?: "left" | "right";
  restoreFocusOnSelect?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLSpanElement>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const initialFocusRef = React.useRef<"first" | "last">("first");
  const menuId = React.useId();
  const triggerProps = trigger.props as React.ButtonHTMLAttributes<HTMLButtonElement>;
  const menuDisabled = Boolean(triggerProps.disabled)
    || !items.some((item) => !item.sep && !item.disabled);

  const enabledItems = React.useCallback(() => itemRefs.current.filter(
    (item): item is HTMLButtonElement => Boolean(item && !item.disabled),
  ), []);

  const close = React.useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (!restoreFocus) return;
    window.requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  }, [triggerRef]);

  React.useEffect(() => {
    if (menuDisabled && open) close(false);
  }, [close, menuDisabled, open]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [close, open]);

  React.useLayoutEffect(() => {
    if (!open) return;
    const candidates = enabledItems();
    const target = initialFocusRef.current === "last" ? candidates[candidates.length - 1] : candidates[0];
    target?.focus({ preventScroll: true });
  }, [enabledItems, open]);

  const moveFocus = (direction: "next" | "previous" | "first" | "last") => {
    const candidates = enabledItems();
    if (!candidates.length) return;
    if (direction === "first" || direction === "last") {
      candidates[direction === "first" ? 0 : candidates.length - 1]?.focus();
      return;
    }
    const current = candidates.indexOf(document.activeElement as HTMLButtonElement);
    const offset = direction === "next" ? 1 : -1;
    candidates[(current + offset + candidates.length) % candidates.length]?.focus();
  };

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus("next");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus("previous");
    } else if (event.key === "Home") {
      event.preventDefault();
      moveFocus("first");
    } else if (event.key === "End") {
      event.preventDefault();
      moveFocus("last");
    } else if (event.key === "Tab") {
      // Start native Tab traversal from the trigger before the menu unmounts.
      triggerRef.current?.focus({ preventScroll: true });
      close(false);
    }
  };

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      {React.cloneElement(trigger as React.ReactElement<any>, {
        id: triggerProps.id ?? `${menuId}-trigger`,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": menuId,
        disabled: menuDisabled,
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          triggerProps.onClick?.(event);
          initialFocusRef.current = "first";
          setOpen((current) => !current);
        },
        onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => {
          triggerProps.onKeyDown?.(event);
          if (event.defaultPrevented || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) return;
          event.preventDefault();
          initialFocusRef.current = event.key === "ArrowUp" ? "last" : "first";
          setOpen(true);
        },
      })}
      {open && (
        <div
          id={menuId}
          className="menu"
          role="menu"
          aria-labelledby={triggerProps.id ?? `${menuId}-trigger`}
          style={{ top: "calc(100% + 4px)", [align]: 0 } as React.CSSProperties}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((it, i) => it.sep
            ? <div key={i} className="menu__sep" role="separator" />
            : <button
                key={i}
                ref={(node) => { itemRefs.current[i] = node; }}
                type="button"
                role={it.checked === undefined ? "menuitem" : "menuitemradio"}
                aria-checked={it.checked}
                tabIndex={-1}
                disabled={it.disabled}
                onClick={() => { close(restoreFocusOnSelect); it.onClick?.(); }}
                style={it.danger ? { color: "var(--danger-text)" } : undefined}
              >
                {it.checked !== undefined && <span aria-hidden="true" style={{ width: 15, display: "inline-flex" }}>{it.checked && <Icon name="check" size={15} />}</span>}
                {it.icon && <Icon name={it.icon} size={15} />}{it.label}
              </button>)}
        </div>
      )}
    </span>
  );
}
